import { sum } from "lodash";
import PropTypes from "prop-types";
import { Table } from "semantic-ui-react";

import dateTimeUtils from "../../../../utils/dateTimeUtils";
import { Tooltip } from "../../../../components/common/tooltip";
import {
  EditLinkButton,
  DeleteLinkButton,
  DuplicateLinkButton,
  ViewContentLinkButton,
} from "../../../../components/buttons/linkButtons";
import { ContentItemMenu, TextTruncate } from "../../../../components";
import { DeletionRestrictedTooltip } from "../../../../components/tooltips/deletionRestrictedTooltip/DeletionRestrictedTooltip";
import Strings from "../../../../enums/strings";
import { PublishStatus } from "components/common/PublishStatus";
import { ContentTypesEnum, PublishedStatusTypes } from "../../../../enums";
import { getColumnOptions } from "./columnOptions";
import { Title } from "../../../../components/listViewTemplates";
import RestrictedByTooltip from "../../../../components/restrictedByTooltip/RestrictedByTooltip";
import { ObjectiveLabel } from "features/Library/Flows/common/ObjectiveLabel/ObjectiveLabel";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { ToggleVisibilityButton } from "components/buttons/linkButtons/toggleVisibilityButton/ToggleVisibilityButton";
import { getContentLinkToEupFromPreview } from "utils/eupUtils";
import cn from "classnames";
import { HiddenContent } from "components/HiddenContent/HiddenContent";

function FlowRow({ flow, hasPermission, buttonHandlers, isDuplicateDisabled }) {
  const {
    id,
    publisher,
    title,
    published,
    dateModified,
    isEditable,
    readonly,
    canBeDeleted,
    dependencies,
    isChecked,
    goals,
    hidden,
    hasBeenPublished,
  } = flow;

  const entityType = "flow";
  const publisherName = publisher || Strings.notAvailable;
  const { packsCount = 0, totalAssignments = 0 } = dependencies || {};
  const dependenciesMap = { packs: packsCount, "users or groups": totalAssignments };
  const hasDeletionDependencies = !!sum(Object.values(dependenciesMap || {}));
  const deleteContentEnabled = useFeatureFlag(FeatureFlags.DeleteContentWithDependenciesFeature);
  const columnOptions = getColumnOptions();
  return (
    <>
      <Table.Cell width={columnOptions[0].width}>
        <HiddenContent variant="list" isHidden={hidden} showIcon>
          <Title url={id.toString()} title={title} className={cn("flow-title normal-color")} clamp={2} />
        </HiddenContent>
      </Table.Cell>
      <Table.Cell width={columnOptions[1].width}>
        <HiddenContent variant="list" isHidden={hidden}>
          <ObjectiveLabel objective={goals?.objective} />
        </HiddenContent>
      </Table.Cell>
      <Table.Cell width={columnOptions[2].width}>
        <HiddenContent variant="list" isHidden={hidden}>
          <Tooltip target={<TextTruncate>{publisherName}</TextTruncate>} content={publisherName} />
        </HiddenContent>
      </Table.Cell>
      <Table.Cell width={columnOptions[3].width}>
        <HiddenContent variant="list" isHidden={hidden}>
          <PublishStatus
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(published, hasBeenPublished)}
          />
        </HiddenContent>
      </Table.Cell>
      <Table.Cell width={columnOptions[4].width}>
        <HiddenContent variant="list" isHidden={hidden}>
          {dateTimeUtils.formatDate(dateModified)}
        </HiddenContent>
      </Table.Cell>
      <Table.Cell className="align-right" width={columnOptions[5].width}>
        <RestrictedByTooltip hasPermission={hasPermission}>
          <ContentItemMenu
            circle
            outlinedEllipsis
            isChecked={isChecked}
            item={{
              isPurchased: !isEditable,
              canBeDeleted,
            }}
            readonly={readonly}
            entityType={entityType}
            dependencies={dependenciesMap}
            purchasedTooltipContent={Strings.tooltipMessages.purchased.oneDeleteDisabled("flow")}
          >
            <ViewContentLinkButton
              href={getContentLinkToEupFromPreview[ContentTypesEnum.Flows](id)}
              disabled={!hasBeenPublished}
            />
            {hasBeenPublished && (
              <ToggleVisibilityButton onClick={buttonHandlers.handleToggleVisibility([id], hidden)} hidden={hidden} />
            )}
            {isEditable && (
              <EditLinkButton onClick={buttonHandlers.handleEditClick(id, published)} isDisabled={!isEditable} />
            )}
            <DuplicateLinkButton onClick={buttonHandlers.handleDuplicateClick([id])} isDisabled={isDuplicateDisabled} />
            {isEditable && (
              <DeleteLinkButton
                isDisabled={(!deleteContentEnabled && !canBeDeleted) || !isEditable}
                after={
                  isEditable &&
                  !deleteContentEnabled &&
                  hasDeletionDependencies && (
                    <DeletionRestrictedTooltip dependencies={dependenciesMap} entityType={entityType} />
                  )
                }
                onClick={buttonHandlers.handleDeleteClick([id], packsCount)}
              />
            )}
          </ContentItemMenu>
        </RestrictedByTooltip>
      </Table.Cell>
    </>
  );
}

FlowRow.defaultProps = {
  isEditable: false,
};

FlowRow.propTypes = {
  flow: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    objective: PropTypes.string,
    objectiveId: PropTypes.number,
    ownerId: PropTypes.number,
    publisher: PropTypes.string,
    published: PropTypes.bool,
    publisherId: PropTypes.number,
    usersCount: PropTypes.number,
    dateCreated: PropTypes.string,
    dateModified: PropTypes.string,
    isEditable: PropTypes.bool,
    readonly: PropTypes.bool,
    canBeDeleted: PropTypes.bool,
    dependencies: PropTypes.object,
    isChecked: PropTypes.bool,
  }),
  buttonHandlers: PropTypes.shape({
    handleEditClick: PropTypes.func,
    handleDuplicateClick: PropTypes.func,
    handleDeleteClick: PropTypes.func,
  }),
  hasPermission: PropTypes.bool,
  isDuplicateDisabled: PropTypes.bool,
};

export default FlowRow;
